import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

/**
 * Dev-режим «показать скелетоны»: `?skeleton=1` в адресе держит ВСЕ запросы
 * в состоянии загрузки, поэтому любой экран можно открыть скелетоном и
 * сверить с обычным видом. Кривую геометрию плейсхолдера иначе не поймать:
 * статический анализ её не видит, а поймать настоящий момент загрузки на
 * быстром соединении невозможно.
 *
 * В прод-сборку не попадает — гейт по `import.meta.env.DEV`.
 */
export const SKELETON_PREVIEW =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('skeleton');

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Перехват на уровне `defaultQueryOptions` — единственная точка, через которую
 * проходят все запросы, включая те, что объявляют свой `queryFn` прямо в
 * компоненте. Промис намеренно никогда не резолвится: `isPending` остаётся
 * true, ретраи выключены, ошибок нет.
 *
 * Патчим инстанс, а не наследуем класс: у `defaultQueryOptions` дженерик-сигнатура
 * с шестью параметрами, и подкласс с ней разъезжается по типам.
 */
if (SKELETON_PREVIEW) {
  const original = queryClient.defaultQueryOptions.bind(queryClient);
  queryClient.defaultQueryOptions = ((options: Parameters<typeof original>[0]) => ({
    ...original(options),
    queryFn: () => new Promise<never>(() => {}),
    retry: false,
  })) as typeof queryClient.defaultQueryOptions;
}

export const persister = SKELETON_PREVIEW
  ? // Восстановленный из sessionStorage кэш закрыл бы скелетон на первом кадре.
    {
      persistClient: async () => {},
      restoreClient: async () => undefined,
      removeClient: async () => {},
    }
  : typeof window !== 'undefined'
    ? createSyncStoragePersister({
        storage: window.sessionStorage,
        key: 'vpnf-rq-cache',
        throttleTime: 3000,
      })
    : undefined;
